import * as BonusCartStepTracker from "@omni/services/tracking/trackers/tools/bonusCard/bonuscardStep.tracker";
import { ReactNode, useEffect, useState } from "react";

import { useCustomerServiceApi } from "@/api/hooks/customerService.hook";
import { SubscribeCustomerToBonusCardRequest } from "@/api/hooks/customerService.types";
import {
    RequestBonusCardAddress,
    RequestBonusCardCustomerModel
} from "@/components/Account/BonusCardRequest/bonusCardRequest.component";
import { BonusCardRequestModal } from "@/components/Account/RequestBonusCard/BonusCardRequestModal/bonusCardRequestModal.component";
import { AddressValidationProvider } from "@/components/Address/AddressValidation/context/addressValidation.context";
import * as BonusCardService from "@/services/bonusCard.service";
import { BonusCardStatus } from "@/utils/enums";

export type RequestBonusCardChildProps = {
    openModal: () => void;
    bonusCardStatus: BonusCardStatus | null;
};

type RequestBonusCardWrapperProps = {
    customer: RequestBonusCardCustomerModel;
    bonusCardStatus: BonusCardStatus;
    address?: RequestBonusCardAddress;
    children: (props: RequestBonusCardChildProps) => ReactNode;
    onSubmit?: () => void;
};

const RequestBonusCardWrapper = ({
    customer,
    bonusCardStatus,
    address,
    children,
    onSubmit
}: RequestBonusCardWrapperProps) => {
    const [isFormOpen, setFormOpen] = useState(false);

    const [{ loading }, subscribeCustomerToBonusCard] = useCustomerServiceApi(
        {
            url: "/customers/bonuscard",
            method: "POST",
            withCredentials: true
        },
        { manual: true, useCache: false }
    );

    const onSubmitBonusCardRequest = async (
        request: SubscribeCustomerToBonusCardRequest
    ) => {
        try {
            const response = await subscribeCustomerToBonusCard({
                data: request
            });

            if (!(response.status >= 200 && response.status < 300)) return;

            BonusCardService.setBonusCardRequestedState(request.email);
            BonusCartStepTracker.trackSubmit(request);
            setFormOpen(false);
            onSubmit?.();
        } catch {
            console.warn("subscription failed");
        }
    };

    useEffect(() => {
        if (isFormOpen) BonusCartStepTracker.trackStart();
    }, [isFormOpen]);

    if (bonusCardStatus === BonusCardStatus.HasCard) {
        return null;
    }

    return (
        <>
            {children({
                openModal: () => setFormOpen(true),
                bonusCardStatus: bonusCardStatus
            })}
            <AddressValidationProvider>
                <BonusCardRequestModal
                    isLoadingRequest={loading}
                    isOpen={isFormOpen}
                    onClose={() => setFormOpen(false)}
                    onSubmit={onSubmitBonusCardRequest}
                    address={address}
                    customer={customer}
                />
            </AddressValidationProvider>
        </>
    );
};

export { RequestBonusCardWrapper };
